import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PartyKindToggle from './PartyKindToggle';
import { syncIdentification } from '../utils/vatNumber';

export const COUNTRIES = ['BE', 'FR', 'NL', 'LU', 'DE', 'GB', 'ES', 'IT', 'PT', 'CH', 'AT', 'IE', 'US', 'CA'];

export const EMPTY_CLIENT = {
  name: '', contact_person: '', email: '', billing_email: '', phone: '',
  address: '', postal_code: '', city: '', country_code: 'BE',
  vat_number: '', company_number: '', kind: 'company',
};

// What a private person (« particulier ») has no use for.
const COMPANY_ONLY = ['contact_person', 'vat_number', 'company_number'];

/** Is this client a « particulier »? */
export const isIndividualClient = (client) => client.kind === 'individual';

/**
 * The body to send for a client (read-only fields left out). A particulier
 * keeps no company data.
 */
export function clientPayload(client) {
  const {
    id, created_at, invoiceninja_client_id, billing_uses, intervention_uses, ...body
  } = client;
  body.kind = client.kind || 'company';
  if (isIndividualClient(client)) COMPANY_ONLY.forEach((k) => { body[k] = ''; });
  return body;
}

/**
 * A fresh record that starts as a copy of `source`: every field the form
 * edits, nothing that belongs to the original (id, InvoiceNinja link, uses).
 */
export function copyOfClient(source) {
  const copy = { ...EMPTY_CLIENT };
  Object.keys(EMPTY_CLIENT).forEach((k) => {
    if (source && source[k] != null && source[k] !== '') copy[k] = source[k];
  });
  return copy;
}

/**
 * Every field of a client, grouped as they are collected: kind, contact,
 * address (country last), then identification, where the VAT and company
 * numbers fill each other. Controlled: `value` / `onChange`.
 */
export default function ClientFields({ value, onChange, idPrefix = 'client' }) {
  const { t } = useTranslation();
  const [filled, setFilled] = useState(null); // which number was just completed for the user
  const individual = isIndividualClient(value);
  const id = (k) => `${idPrefix}-${k}`;

  const set = (k, v) => onChange({ ...value, [k]: v });

  const setIdentification = (k, v) => {
    const next = syncIdentification(value, k, v, COUNTRIES);
    onChange(next);
    if (k === 'vat_number') setFilled(next.company_number ? 'companyNumber' : null);
    else if (next.vat_number && next.vat_number !== value.vat_number) setFilled('vatNumber');
    else if (k !== 'country_code') setFilled(null);
  };

  // A render *function* (not a component) so inputs keep focus across keystrokes.
  const field = (label, k, { type = 'text', full = false, onInput = set, autoComplete } = {}) => (
    <div className={`client-field${full ? ' client-field-full' : ''}`}>
      <label htmlFor={id(k)}>{label}</label>
      <input
        id={id(k)}
        className="form-control"
        type={type}
        value={value[k] || ''}
        autoComplete={autoComplete}
        onChange={(e) => onInput(k, e.target.value)}
      />
    </div>
  );

  return (
    <div className="client-fields">
      <PartyKindToggle name={id('kind')} value={value.kind} onChange={(v) => set('kind', v)} />

      <fieldset className="client-group">
        <legend>{t('clients.groupContact')}</legend>
        <div className="client-grid">
          {field(`${t('clients.name')} *`, 'name', { full: individual, autoComplete: 'off' })}
          {!individual && field(t('clients.contactPerson'), 'contact_person')}
          {field(t('clients.email'), 'email', { type: 'email' })}
          {field(t('clients.billingEmail'), 'billing_email', { type: 'email' })}
          {field(t('clients.phone'), 'phone', { type: 'tel' })}
        </div>
      </fieldset>

      <fieldset className="client-group">
        <legend>{t('clients.groupAddress')}</legend>
        <div className="client-grid">
          {field(t('clients.address'), 'address', { full: true })}
          {field(t('clients.postalCode'), 'postal_code')}
          {field(t('clients.city'), 'city')}
          <div className="client-field">
            <label htmlFor={id('country_code')}>{t('clients.country')}</label>
            <select
              id={id('country_code')}
              className="form-control"
              value={value.country_code || 'BE'}
              onChange={(e) => setIdentification('country_code', e.target.value)}
            >
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </fieldset>

      {!individual && (
        <fieldset className="client-group">
          <legend>{t('clients.groupIdentification')}</legend>
          <div className="client-grid">
            {field(t('clients.vatNumber'), 'vat_number', { onInput: setIdentification })}
            {field(t('clients.companyNumber'), 'company_number', { onInput: setIdentification })}
          </div>
          <p className="client-hint" aria-live="polite">
            {filled ? t(`clients.filled_${filled}`) : t('clients.identificationHint')}
          </p>
        </fieldset>
      )}
    </div>
  );
}
